import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import {
  Namespace,
  Session,
  SessionEvent,
  SessionEventType,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import {
  getSession$,
  listSessions$,
} from '@vendasta/protos/ai_assistants/v1alpha1/session-service';
import { PagedRequestOptions } from '@vendasta/protos/vendastatypes';

export interface ListSessionsPage {
  readonly sessions: Session[];
  readonly nextCursor: string;
}

@Injectable({ providedIn: 'root' })
export class SessionHistoryService {
  listSessions$(
    namespace: Namespace,
    tags: string[],
    pagingOptions: Partial<PagedRequestOptions>,
  ): Observable<ListSessionsPage> {
    return listSessions$({
      namespace,
      filters: { tags },
      pagingOptions,
    }).pipe(
      map((response) => ({
        sessions: [...response.sessions],
        nextCursor: response.metadata?.nextCursor ?? '',
      })),
    );
  }

  getLastMessagePreview$(namespace: Namespace, sessionId: string): Observable<string | null> {
    return getSession$({
      sessionId,
      namespace,
      pagingOptions: { pageSize: 25 },
      eventTypeFilter: SessionEventType.SESSION_EVENT_TYPE_MESSAGE,
    }).pipe(
      map((response) => {
        const events = response.events ?? [];
        let latest: { content: string; created: Date } | null = null;
        for (const event of events) {
          const message = event.message
          let content = message?.content;
          if (!content) {
            content = message?.contentParts?.length ? message?.contentParts[0].text: '';
          }
          if (!content) continue;
          const created = event.created;
          if (!created) continue;
          if (!latest || created > latest.created) {
            latest = { content, created };
          }
        }
        return latest?.content ?? null;
      }),
      catchError(() => of(null)),
    );
  }

  getSessionEvents$(
    namespace: Namespace,
    sessionId: string,
    pageSize: number,
  ): Observable<SessionEvent[]> {
    return getSession$({
      sessionId,
      namespace,
      pagingOptions: { pageSize },
    }).pipe(map((response) => [...response.events]));
  }
}
